import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export const TwoFAVerify = () => {
  const { verify2FA } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(code);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="2fa-verify" className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white">Two-Factor Authentication</h2>
          <p className="text-gray-400 mt-2">Enter the code from your authenticator app</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            data-testid="2fa-code-input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full text-center text-3xl tracking-[0.5em] bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            maxLength={6}
            autoFocus
          />
          
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          
          <button
            data-testid="2fa-verify-btn"
            type="submit"
            disabled={code.length !== 6 || loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-lg transition-colors"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );
};


export const TwoFASetup = () => {
  const { setup2FA, confirm2FA, logout } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('loading'); // loading, scan, verify, done

  useEffect(() => {
    const init = async () => {
      try {
        const data = await setup2FA();
        setQrData(data);
        setStep('scan');
      } catch (err) {
        setError('Failed to generate 2FA setup. Please try again.');
        setStep('error');
      }
    };
    init();
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirm2FA(code);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="2fa-setup" className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white">Set Up 2FA</h2>
          <p className="text-gray-400 mt-2">Two-factor authentication is required for all accounts</p>
        </div>

        {step === 'loading' && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-400 mt-4">Generating your 2FA secret...</p>
          </div>
        )}

        {step === 'scan' && qrData && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-300 text-sm mb-4">
                1. Open <strong>Google Authenticator</strong> (or any TOTP app)<br />
                2. Scan this QR code
              </p>
              <div className="bg-white rounded-lg p-4 inline-block">
                <img src={qrData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Manual entry key:</p>
              <p className="text-green-400 font-mono text-sm break-all select-all">{qrData.secret}</p>
            </div>

            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm block mb-1">3. Enter the 6-digit code from your app:</label>
                <input
                  data-testid="2fa-setup-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full text-center text-2xl tracking-[0.5em] bg-gray-800 border border-gray-700 rounded-lg p-3 text-white font-mono focus:ring-2 focus:ring-green-500"
                  maxLength={6}
                />
              </div>
              
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
              
              <button
                data-testid="2fa-confirm-btn"
                type="submit"
                disabled={code.length !== 6 || loading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                {loading ? 'Verifying...' : 'Activate 2FA'}
              </button>
            </form>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white">2FA Activated!</h3>
            <p className="text-gray-400 mt-2">Your account is now protected.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Continue to Dashboard
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-8">
            <p className="text-red-400">{error}</p>
            <button onClick={logout} className="mt-4 text-gray-400 hover:text-white underline">
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
